import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { getRouterSelectors } from '@ngrx/router-store';
import { Action, Store } from '@ngrx/store';
import { Observable, tap } from 'rxjs';

import { InfraPreset, Stage } from '@mobi/rwc-utils-ng-jslib';
import { TealiumTrackingService } from '@mobi/tea-tagmanager-ng-jslib';

import { Helper } from '../../helper';

import { tealiumTrackingActions } from './tealium-tracking.actions';

type TrackChangeLanguageAction = ReturnType<typeof tealiumTrackingActions.trackChangeLanguage>;
type TrackPageViewAction = ReturnType<typeof tealiumTrackingActions.trackPageView>;
type TrackDocumentDownloadEventAction = ReturnType<typeof tealiumTrackingActions.trackDocumentDownloadEvent>;
type TrackButtonClickEventAction = ReturnType<typeof tealiumTrackingActions.trackButtonClickEvent>;
type TrackInteractionEventAction = ReturnType<typeof tealiumTrackingActions.trackInteractionEvent>;
type AddVolatileAction = ReturnType<typeof tealiumTrackingActions.addVolatile>;

const GERMAN_DOMAIN_URLS = {
    prod: {
        b2e: {
            protekta: 'https://b2e.oiv-protekta.mobiliar.ch/de',
            mobiliar: 'https://b2e.oiv.mobiliar.ch/de',
        },
        b2c: {
            protekta: 'https://oiv-protekta.mobiliar.ch',
            mobiliar: 'https://oiv.mobiliar.ch',
        },
    },
    preprod: {
        b2e: {
            protekta: 'https://b2e.oiv-protekta.mobiliar-preprod.ch/de',
            mobiliar: 'https://b2e.oiv.app.mobiliar-preprod.ch/de',
        },
        b2c: {
            protekta: 'https://oiv-protekta.mobiliar-preprod.ch',
            mobiliar: 'https://oiv-viewer-b2c.app.mobiliar-preprod.ch',
        },
    },
} as const;

@Injectable()
export class TealiumTrackingEffects {
    #store = inject(Store);
    #actions$ = inject(Actions) as unknown as Observable<Action>;
    #tealiumTrackingService = inject(TealiumTrackingService);
    #helper = inject(Helper);

    $trackChangeLanguage = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(tealiumTrackingActions.trackChangeLanguage),
                concatLatestFrom(() => this.#store.select(getRouterSelectors().selectUrl)),
                tap(([action, url]: [TrackChangeLanguageAction, string]) => {
                    this.#tealiumTrackingService.addVolatile('language_code', action.language);
                    this.#tealiumTrackingService.trackLinkOnsiteEvent({
                        link_category: 'OnSiteLink - Footer',
                        link_action: 'click',
                        link_name: action.language,
                        link_target: url,
                    });
                }),
            ),
        {
            dispatch: false,
        },
    );

    getGermanDomainUrl(stage?: Stage, infraPreset?: InfraPreset, tkNameId?: string): string {
        const environment = stage === 'prod' ? 'prod' : 'preprod';
        const channel = infraPreset === 'b2e' ? 'b2e' : 'b2c';
        const tkName = tkNameId?.includes('protekta') ? 'protekta' : 'mobiliar';

        return GERMAN_DOMAIN_URLS[environment][channel][tkName];
    }

    $trackPageView = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(tealiumTrackingActions.trackPageView),
                concatLatestFrom(() => this.#store.select(getRouterSelectors().selectUrl)),
                tap(([{ title, customEvent }, url]: [TrackPageViewAction, string]) => {
                    const stage = (window.document.defaultView as Window).MOBI_RWC_CONFIG?.stage;
                    const infraPreset = (window.document.defaultView as Window).MOBI_RWC_CONFIG?.infraPreset;
                    const tkNameId = (window.document.defaultView as Window).MOBI_RWC_CONFIG?.tkNameId;
                    const germanDomainUrl = this.getGermanDomainUrl(stage, infraPreset, tkNameId);

                    const urlOnly = this.#helper.removeFragmentAndQuery(url);
                    const avbStartIndex = urlOnly.indexOf('/avb');
                    const pathFromAvb = urlOnly.slice(avbStartIndex);
                    const alternateUrl = germanDomainUrl + pathFromAvb;

                    const payload = customEvent
                        ? { ...customEvent, page_name: title, page_url_alternate: alternateUrl }
                        : {
                              page_url: urlOnly,
                              page_cleanURL: urlOnly,
                              title,
                              page_name: title,
                              page_type: 'content_page',
                              page_url_alternate: alternateUrl,
                          };

                    this.#tealiumTrackingService.trackPageView(payload);
                }),
            ),
        {
            dispatch: false,
        },
    );

    $trackDocumentDownloadEvent = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(tealiumTrackingActions.trackDocumentDownloadEvent),
                tap(({ filetype, title, downloadUrl }: TrackDocumentDownloadEventAction) => {
                    this.#tealiumTrackingService.trackDocumentDownloadEvent({
                        download_filetype: filetype,
                        download_title: title,
                        download_url: downloadUrl,
                    });
                }),
            ),
        {
            dispatch: false,
        },
    );

    $trackButtonClickEvent = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(tealiumTrackingActions.trackButtonClickEvent),
                tap(({ buttonName, buttonTarget }: TrackButtonClickEventAction) => {
                    this.#tealiumTrackingService.trackButtonClickEvent({
                        button_name: buttonName,
                        button_target: buttonTarget,
                    });
                }),
            ),
        {
            dispatch: false,
        },
    );

    $trackInteractionEvent = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(tealiumTrackingActions.trackInteractionEvent),
                tap(({ elementName, elementType, elementValue, interactionType }: TrackInteractionEventAction) => {
                    this.#tealiumTrackingService.trackInteractionEvent({
                        element_name: elementName,
                        element_type: elementType,
                        element_value: elementValue,
                        interaction_type: interactionType,
                    });
                }),
            ),
        {
            dispatch: false,
        },
    );

    $addVolatile = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(tealiumTrackingActions.addVolatile),
                tap(({ key, value }: AddVolatileAction) => {
                    this.#tealiumTrackingService.addVolatile(key, value);
                }),
            ),
        {
            dispatch: false,
        },
    );
}
